import { useState, useEffect, useRef } from 'react';
import { vendorPaymentAPI } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';
import { toast } from 'sonner';
import { Loader2, Search, Upload, CloudDownload, CheckCircle2 } from 'lucide-react';

export function CreateRequestModal({ open, onClose, onCreated }) {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [selectedPO, setSelectedPO] = useState(null);
  const [poMissing, setPoMissing] = useState(false);
  const [fetchingPO, setFetchingPO] = useState(false);
  const [file, setFile] = useState(null);
  const [supportingFiles, setSupportingFiles] = useState([]);
  const [amount, setAmount] = useState('');
  const [paymentType, setPaymentType] = useState('Advance');
  const [dueDate, setDueDate] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const searchTimer = useRef(null);

  useEffect(() => {
    if (!open) {
      setQuery(''); setSuggestions([]); setSelectedPO(null); setPoMissing(false);
      setFile(null); setAmount(''); setPaymentType('Advance'); setDueDate(''); setNotes('');
      setSupportingFiles([]);
    }
  }, [open]);

  const handleQueryChange = (val) => {
    setQuery(val);
    setSelectedPO(null);
    setPoMissing(false);
    clearTimeout(searchTimer.current);
    if (val.trim().length < 2) { setSuggestions([]); return; }
    searchTimer.current = setTimeout(async () => {
      try {
        const res = await vendorPaymentAPI.searchPO(val.trim());
        const results = res.data || [];
        setSuggestions(results);
        // No local matches — if it looks like a real PO number, check it directly so the
        // "not in mirror / fetch from Zoho" path is reachable without an extra click.
        if (results.length === 0 && /^[A-Za-z0-9-]+\/[A-Za-z0-9-]+$/.test(val.trim())) {
          await selectPO(val.trim());
        }
      } catch {
        toast.error('PO search failed — the Zoho mirror may be unavailable');
      }
    }, 300);
  };

  const selectPO = async (poNumber) => {
    setSuggestions([]);
    setQuery(poNumber);
    setPoMissing(false);
    try {
      const res = await vendorPaymentAPI.getPO(poNumber);
      setSelectedPO(res.data.po);
      setAmount(res.data.po.total ? String(res.data.po.total) : '');
    } catch (err) {
      if (err.response?.status === 404 && err.response?.data?.detail?.not_in_mirror) {
        setPoMissing(true);
        setSelectedPO(null);
      } else {
        toast.error(err.response?.data?.detail?.message || err.response?.data?.detail || 'PO lookup failed');
      }
    }
  };

  const handleFetchFromZoho = async () => {
    setFetchingPO(true);
    try {
      const res = await vendorPaymentAPI.fetchPOFromZoho(query.trim());
      setSelectedPO(res.data.po);
      setAmount(res.data.po.total ? String(res.data.po.total) : '');
      setPoMissing(false);
      toast.success('PO fetched from Zoho');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not fetch PO from Zoho');
    } finally {
      setFetchingPO(false);
    }
  };

  const handleSubmit = async () => {
    if (!selectedPO || !file || !amount || !dueDate) {
      toast.error('PO, PI file, amount and due date are all required');
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('po_number', selectedPO.po_number);
      fd.append('amount_requested', amount);
      fd.append('payment_type', paymentType);
      fd.append('due_date', dueDate);
      fd.append('notes', notes);
      fd.append('file', file);
      const res = await vendorPaymentAPI.create(fd);
      if (supportingFiles.length > 0) {
        try {
          await vendorPaymentAPI.addSupportingFiles(res.data.id, supportingFiles);
        } catch (err) {
          toast.error(err.response?.data?.detail || 'Request created, but supporting files failed to attach');
        }
      }
      toast.success('Request created — running PI vs PO check...');
      onCreated(res.data.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create request');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid="create-vp-request-modal">
        <DialogHeader><DialogTitle>New Vendor Payment Request</DialogTitle></DialogHeader>
        <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
          <div className="relative">
            <Label>Purchase Order Number</Label>
            <div className="relative mt-1">
              <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
              <Input value={query} onChange={(e) => handleQueryChange(e.target.value)} placeholder="Search PO number..." className="pl-8" data-testid="po-search-input" />
            </div>
            {suggestions.length > 0 && (
              <div className="absolute z-10 mt-1 w-full bg-popover border border-border rounded-sm shadow-lg max-h-48 overflow-y-auto">
                {suggestions.map((s) => (
                  <button key={s.po_number} onClick={() => selectPO(s.po_number)} data-testid={`po-suggestion-${s.po_number}`}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-muted border-b border-border last:border-0">
                    <div className="font-mono font-medium">{s.po_number}</div>
                    <div className="text-xs text-muted-foreground truncate">{s.vendor_name} · {s.currency_code} {s.total}</div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {poMissing && (
            <div className="rounded-sm border border-amber-300 bg-amber-50 p-3 text-sm" data-testid="po-not-in-mirror">
              <p className="text-amber-900">This PO was not found in the local Zoho mirror.</p>
              <Button size="sm" variant="outline" className="mt-2 rounded-sm" onClick={handleFetchFromZoho} disabled={fetchingPO} data-testid="fetch-po-from-zoho-btn">
                {fetchingPO ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <CloudDownload className="h-3.5 w-3.5 mr-1.5" />}
                Fetch this PO from Zoho
              </Button>
              <p className="text-[11px] text-amber-700 mt-1">Uses one Zoho API call, counted against the daily cap.</p>
            </div>
          )}

          {selectedPO && (
            <div className="rounded-sm border border-emerald-200 bg-emerald-50 p-3 text-sm" data-testid="selected-po-summary">
              <div className="flex items-center gap-1.5 text-emerald-800 font-medium"><CheckCircle2 className="h-3.5 w-3.5" />{selectedPO.po_number}</div>
              <p className="text-emerald-900 mt-0.5">{selectedPO.vendor_name}</p>
              <p className="text-xs text-emerald-700">{selectedPO.currency_code} {selectedPO.total} · status: {selectedPO.status}</p>
            </div>
          )}

          <div>
            <Label>PI Document (Excel, CSV, PDF, or image)</Label>
            <div className="mt-1 flex items-center gap-2">
              <Input type="file" accept=".xlsx,.xls,.csv,.pdf,.png,.jpg,.jpeg" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="pi-file-input" />
            </div>
            {file && <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1"><Upload className="h-3 w-3" />{file.name}</p>}
          </div>

          <div>
            <Label>Supporting files (optional — e.g. vendor's Excel parts list)</Label>
            <div className="mt-1 flex items-center gap-2">
              <Input type="file" multiple accept=".xlsx,.xls,.csv,.pdf,.png,.jpg,.jpeg"
                onChange={(e) => setSupportingFiles([...(supportingFiles), ...Array.from(e.target.files || [])].slice(0, 5))}
                data-testid="supporting-files-input" />
            </div>
            {supportingFiles.length > 0 && (
              <ul className="mt-1 space-y-0.5" data-testid="supporting-files-selected-list">
                {supportingFiles.map((f, i) => (
                  <li key={i} className="text-xs text-muted-foreground flex items-center justify-between gap-2">
                    <span className="flex items-center gap-1 truncate"><Upload className="h-3 w-3 shrink-0" />{f.name}</span>
                    <button type="button" className="text-red-500 hover:underline shrink-0" data-testid={`supporting-file-remove-${i}`}
                      onClick={() => setSupportingFiles(supportingFiles.filter((_, idx) => idx !== i))}>Remove</button>
                  </li>
                ))}
              </ul>
            )}
            <p className="text-[11px] text-muted-foreground mt-1">Max 5 files, 25MB each. Reference only — never used for the PI check.</p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Amount Requested</Label>
              <Input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="amount-requested-input" />
            </div>
            <div>
              <Label>Payment Type</Label>
              <Select value={paymentType} onValueChange={setPaymentType}>
                <SelectTrigger data-testid="payment-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Advance">Advance</SelectItem>
                  <SelectItem value="Balance">Balance</SelectItem>
                  <SelectItem value="Full">Full</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Due Date</Label>
            <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} data-testid="due-date-input" />
          </div>
          <div>
            <Label>Notes (optional)</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} data-testid="notes-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-sm" onClick={onClose}>Cancel</Button>
          <Button className="rounded-sm" disabled={submitting} onClick={handleSubmit} data-testid="create-vp-request-submit">
            {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null}
            Upload &amp; Check PI
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
